import React from "react";
import { Link, useLocation } from "react-router-dom";
import { useAuth, logout } from "wasp/client/auth";
import { useNavigation } from "./NavigationProvider";
import { Badge } from "../ui/badge";
import { Button } from "../ui/button";
import { routes } from "../../../shared/navigation/routes";
import {
  IuiSidebarProvider,
  IuiSidebar,
  IuiSidebarHeader,
  IuiSidebarContent,
  IuiSidebarFooter,
  IuiSidebarGroup,
  IuiSidebarGroupLabel,
  IuiSidebarMenu,
  IuiSidebarMenuItem,
  IuiSidebarItem,
  IuiSidebarMenuBadge,
  IuiSidebarSeparator,
  useIuiSidebar,
} from "../../intentui/sidebar/index";
import {
  useAuthenticatedProfileProgress,
  selectProgress,
} from "../../../stores/ProfileProgressStore";
import {
  LayoutDashboard,
  Search,
  FileText,
  User,
  CreditCard,
  BarChart3,
  Zap,
  FileStack,
  Brain,
  DollarSign,
  Settings,
  Calendar,
  BookOpen,
  Shield,
  LogOut,
  Flag,
  ShoppingCart,
} from "lucide-react";

type NavItem = {
  id: string;
  label: string;
  icon: React.ComponentType<{ className?: string }>;
  to?: string;
  onClick?: () => void;
  badge?: string | number;
  progress?: number;
};

interface NavigationItemProps {
  item: NavItem;
  isActive: boolean;
  onNavigate?: () => void;
}

function NavigationItem({ item, isActive, onNavigate }: NavigationItemProps) {
  const Icon = item.icon;
  const { state } = useIuiSidebar();
  const isCollapsed = state === "collapsed";

  const handleClick = () => {
    item.onClick?.();
    onNavigate?.();
  };

  const content = (
    <>
      <Icon className="h-5 w-5" />
      {!isCollapsed && (
        <>
          <span className="flex-1">{item.label}</span>
          {item.badge && (
            <IuiSidebarMenuBadge className="bg-coral text-white">
              {typeof item.badge === 'number' && item.badge > 9 ? '9+' : item.badge}
            </IuiSidebarMenuBadge>
          )}
          {typeof item.progress === "number" && (
            <div className="flex items-center gap-1">
              <div className="h-1 w-12 rounded-full bg-neutral-200">
                <div
                  className="h-1 rounded-full bg-coral transition-all duration-200"
                  style={{ width: `${Math.max(0, Math.min(100, item.progress))}%` }}
                />
              </div>
              <span className="text-xs text-supporting">{item.progress}%</span>
            </div>
          )}
        </>
      )}
    </>
  );

  if (item.to) {
    return (
      <IuiSidebarMenuItem>
        <IuiSidebarItem
          asChild
          size="lg"
          isActive={isActive}
          tooltip={isCollapsed ? item.label : undefined}
          className={
            isActive
              ? "border-l-2 border-coral bg-coral/10 text-coral hover:bg-coral/15 data-[active=true]:bg-coral/10 data-[active=true]:text-coral"
              : "text-dusk hover:bg-stone/50 hover:text-ink"
          }
        >
          <Link to={item.to as any} onClick={handleClick}>
            {content}
          </Link>
        </IuiSidebarItem>
      </IuiSidebarMenuItem>
    );
  }

  return (
    <IuiSidebarMenuItem>
      <IuiSidebarItem
        size="lg"
        onClick={handleClick}
        isActive={isActive}
        tooltip={isCollapsed ? item.label : undefined}
        className={
          isActive
            ? "border-l-2 border-coral bg-coral/10 text-coral hover:bg-coral/15 data-[active=true]:bg-coral/10 data-[active=true]:text-coral"
            : "text-dusk hover:bg-stone/50 hover:text-ink"
        }
      >
        {content}
      </IuiSidebarItem>
    </IuiSidebarMenuItem>
  );
}

interface SidebarContentInnerProps {
  onNavigate?: () => void;
}

function SidebarContentInner({ onNavigate }: SidebarContentInnerProps) {
  const { userRole, switchRole, setAdminMode } = useNavigation();
  const { data: user } = useAuth();
  const location = useLocation();
  const profileProgress = useAuthenticatedProfileProgress(selectProgress);
  const { state } = useIuiSidebar();
  const isCollapsed = state === "collapsed";
  const isAdmin = userRole === "admin";
  const canAccessAdmin = user?.isAdmin;

  // Get user's subscription tier
  const userTier = (user as any)?.subscriptionTier || "free_trial";
  const tierDisplayNames = {
    free_trial: "Free Trial",
    duo: "Duo Plan",
    family: "Family Plan",
    master_chef: "Master Chef",
  };
  const currentTierName =
    tierDisplayNames[userTier as keyof typeof tierDisplayNames] || "Free Trial";

  // Helper to check if route is active
  const isRouteActive = (path: string) => {
    if (path === "/") return location.pathname === "/";
    return location.pathname.startsWith(path);
  };

  // Auto-switch sidebar mode based on route when admin access is available
  React.useEffect(() => {
    if (!canAccessAdmin) return;
    const onAdminRoute = location.pathname.startsWith('/admin');
    if (onAdminRoute && userRole !== 'admin') {
      try { setAdminMode(true); } catch {}
      switchRole('admin');
    } else if (!onAdminRoute && userRole !== 'user') {
      switchRole('user');
    }
  }, [location.pathname, canAccessAdmin, userRole, switchRole, setAdminMode]);

  // User Navigation Items
  const userMainItems: NavItem[] = [
    {
      id: "dashboard",
      label: "Dashboard",
      icon: LayoutDashboard,
      to: routes.dashboard,
    },
    {
      id: "discover",
      label: "Discover",
      icon: Search,
      to: routes.discover,
    },
    {
      id: "myrecipes",
      label: "My Recipes",
      icon: BookOpen,
      to: routes.myRecipes,
    },
    {
      id: "plans",
      label: "Plans",
      icon: FileText,
      to: routes.plans,
    },
    {
      id: "shopping",
      label: "Shopping List",
      icon: ShoppingCart,
      to: routes.shoppingList,
    },
  ];

  const userAccountItems: NavItem[] = [
    // Admin entry: one button to enter admin mode and show admin menu (admin-only)
    ...(
      canAccessAdmin
        ? [{
            id: 'admin',
            label: 'Admin',
            icon: Shield,
            to: routes.adminOverview,
            onClick: () => { try { setAdminMode(true); } catch {}; switchRole('admin'); }
          } as NavItem]
        : []
    ),
    {
      id: "profile",
      label: "Kitchen Profile",
      icon: User,
      progress: profileProgress?.overallPct || 0,
      to: routes.kitchenProfile,
    },
    {
      id: "billing",
      label: "Account & Billing",
      icon: CreditCard,
      badge: userTier === "free_trial" ? "Upgrade" : undefined,
      to: "/settings?section=billing",
    },
    {
      id: "pricing",
      label: "Plans & Pricing",
      icon: Zap,
      badge: userTier === "free_trial" ? "New" : undefined,
      to: "/pricing",
    },
    {
      id: "settings",
      label: "Settings",
      icon: Settings,
      to: "/settings",
    },
  ];

  // Admin Navigation Items
  const adminAnalyticsItems: NavItem[] = [
    {
      id: "overview",
      label: "Overview",
      icon: BarChart3,
      to: "/admin/overview",
    },
    {
      id: "usage",
      label: "Usage",
      icon: Zap,
      to: "/admin/usage-management",
    },
  ];

  const adminContentItems: NavItem[] = [
    {
      id: "plans-admin",
      label: "Plans",
      icon: FileStack,
      to: "/admin/plans",
    },
    {
      id: "ai",
      label: "AI Configuration",
      icon: Brain,
      to: "/admin/ai",
    },
  ];

  const adminBusinessItems: NavItem[] = [
    {
      id: "billing",
      label: "Billing",
      icon: DollarSign,
      badge: 2,
      to: "/admin/billing",
    },
    {
      id: "system",
      label: "System",
      icon: Settings,
      to: "/admin/system",
    },
    {
      id: "events",
      label: "Events",
      icon: Calendar,
      to: "/admin/events",
    },
    {
      id: "feature-flags",
      label: "Feature Flags",
      icon: Flag,
      to: "/admin/feature-flags",
    },
  ];

  return (
    <>
      <IuiSidebarHeader className="border-b border-border/20">
        {!isCollapsed && <div className="mb-4 text-2xl font-bold text-dusk">nura</div>}

        {canAccessAdmin && (
          <div>
            {isCollapsed ? (
              <div className="flex flex-col space-y-1">
                <button
                  onClick={() => switchRole("user")}
                  className={`w-full rounded-md p-2 text-xs font-medium transition-all duration-200 ${
                    !isAdmin
                      ? "bg-coral/10 text-coral shadow-sm"
                      : "text-supporting hover:bg-stone/50 hover:text-coral"
                  }`}
                  title="User View"
                >
                  U
                </button>
                <button
                  onClick={() => switchRole("admin")}
                  className={`w-full rounded-md p-2 text-xs font-medium transition-all duration-200 ${
                    isAdmin
                      ? "bg-coral/10 text-coral shadow-sm"
                      : "text-supporting hover:bg-stone/50 hover:text-coral"
                  }`}
                  title="Admin View"
                >
                  A
                </button>
              </div>
            ) : (
              <div className="flex rounded-lg bg-neutral-100 p-1">
                <button
                  onClick={() => switchRole("user")}
                  className={`flex-1 rounded-md px-3 py-2 text-sm font-medium transition-all duration-200 ${
                    !isAdmin
                      ? "bg-white text-dusk shadow-sm"
                      : "text-supporting hover:bg-neutral-200 hover:text-dusk"
                  }`}
                >
                  User
                </button>
                <button
                  onClick={() => switchRole("admin")}
                  className={`flex-1 rounded-md px-3 py-2 text-sm font-medium transition-all duration-200 ${
                    isAdmin
                      ? "bg-white text-dusk shadow-sm"
                      : "text-supporting hover:bg-neutral-200 hover:text-dusk"
                  }`}
                >
                  Admin
                </button>
              </div>
            )}
          </div>
        )}
      </IuiSidebarHeader>

      <IuiSidebarContent>
        {/* Sidebar should not render page breadcrumbs; those belong in content header */}

        {!isAdmin ? (
          <>
            {/* User View - Main Section */}
            <IuiSidebarGroup>
              {!isCollapsed && (
                <IuiSidebarGroupLabel className="text-xs font-semibold uppercase tracking-wider text-supporting">
                  Main
                </IuiSidebarGroupLabel>
              )}
              <IuiSidebarMenu>
                {userMainItems.map((item) => (
                  <NavigationItem
                    key={item.id}
                    item={item}
                    isActive={isRouteActive(item.to || "")}
                    onNavigate={onNavigate}
                  />
                ))}
              </IuiSidebarMenu>
            </IuiSidebarGroup>

            <IuiSidebarSeparator className="bg-border/20" />

            {/* User View - Account Section */}
            <IuiSidebarGroup>
              {!isCollapsed && (
                <IuiSidebarGroupLabel className="text-xs font-semibold uppercase tracking-wider text-supporting">
                  Account
                </IuiSidebarGroupLabel>
              )}
              <IuiSidebarMenu>
                {userAccountItems.map((item) => (
                  <NavigationItem
                    key={item.id}
                    item={item}
                    isActive={isRouteActive(item.to || "")}
                    onNavigate={onNavigate}
                  />
                ))}
              </IuiSidebarMenu>
            </IuiSidebarGroup>
          </>
        ) : (
          <>
            {/* Admin View - Analytics Section */}
            <IuiSidebarGroup>
              {!isCollapsed && (
                <IuiSidebarGroupLabel className="text-xs font-semibold uppercase tracking-wider text-supporting">
                  Analytics
                </IuiSidebarGroupLabel>
              )}
              <IuiSidebarMenu>
                {adminAnalyticsItems.map((item) => (
                  <NavigationItem
                    key={item.id}
                    item={item}
                    isActive={isRouteActive(item.to || "")}
                    onNavigate={onNavigate}
                  />
                ))}
              </IuiSidebarMenu>
            </IuiSidebarGroup>

            <IuiSidebarSeparator className="bg-border/20" />

            {/* Admin View - Content Section */}
            <IuiSidebarGroup>
              {!isCollapsed && (
                <IuiSidebarGroupLabel className="text-xs font-semibold uppercase tracking-wider text-supporting">
                  Content & AI
                </IuiSidebarGroupLabel>
              )}
              <IuiSidebarMenu>
                {adminContentItems.map((item) => (
                  <NavigationItem
                    key={item.id}
                    item={item}
                    isActive={isRouteActive(item.to || "")}
                    onNavigate={onNavigate}
                  />
                ))}
              </IuiSidebarMenu>
            </IuiSidebarGroup>

            <IuiSidebarSeparator className="bg-border/20" />

            {/* Admin View - Business Section */}
            <IuiSidebarGroup>
              {!isCollapsed && (
                <IuiSidebarGroupLabel className="text-xs font-semibold uppercase tracking-wider text-supporting">
                  Business
                </IuiSidebarGroupLabel>
              )}
              <IuiSidebarMenu>
                {adminBusinessItems.map((item) => (
                  <NavigationItem
                    key={item.id}
                    item={item}
                    isActive={isRouteActive(item.to || "")}
                    onNavigate={onNavigate}
                  />
                ))}
              </IuiSidebarMenu>
            </IuiSidebarGroup>
          </>
        )}
      </IuiSidebarContent>

      <IuiSidebarFooter className="border-t border-border/20">
        {isCollapsed ? (
          <div className="flex flex-col items-center space-y-3">
            {/* Collapsed Profile Photo */}
            <div
              className="flex h-10 w-10 items-center justify-center rounded-full border-2 border-white bg-gradient-to-br from-coral to-sage shadow-sm"
              title={`${(user as any)?.firstName || (user as any)?.displayName || user?.email?.split("@")[0] || "Chef"} - ${currentTierName}`}
            >
              <span className="text-sm font-semibold text-white">
                {(
                  (user as any)?.firstName?.charAt(0) ||
                  (user as any)?.displayName?.charAt(0) ||
                  user?.email?.charAt(0) ||
                  "U"
                ).toUpperCase()}
              </span>
            </div>

            {/* Collapsed Logout */}
            <Button
              variant="ghost"
              size="sm"
              onClick={() => logout()}
              className="h-8 w-full p-0 text-supporting hover:bg-coral/5 hover:text-coral"
              title="Sign out"
            >
              <LogOut className="h-4 w-4" />
            </Button>
          </div>
        ) : (
          <div className="space-y-3">
            {/* User Info */}
            <div className="flex items-center gap-3">
              <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-full border-2 border-white bg-gradient-to-br from-coral to-sage shadow-sm">
                <span className="text-sm font-semibold text-white">
                  {(
                    (user as any)?.firstName?.charAt(0) ||
                    (user as any)?.displayName?.charAt(0) ||
                    user?.email?.charAt(0) ||
                    "U"
                  ).toUpperCase()}
                </span>
              </div>
              <div className="min-w-0 flex-1">
                <div className="truncate text-sm font-medium text-ink">
                  {(user as any)?.firstName ||
                    (user as any)?.displayName ||
                    user?.email?.split("@")[0] ||
                    "Chef"}
                </div>
                <div className="truncate text-xs text-supporting">Active member</div>
              </div>
            </div>

            {/* Plan Tier Badge */}
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Zap className="h-4 w-4 flex-shrink-0 text-warning" />
                <span className="text-sm font-medium text-dusk">Current Plan</span>
              </div>
              <Badge
                className={`px-2 py-1 text-xs font-semibold ${
                  userTier === "master_chef"
                    ? "bg-gradient-to-r from-coral to-sage text-white"
                    : userTier === "free_trial"
                      ? "bg-warning text-ink"
                      : "bg-coral text-white"
                }`}
              >
                {currentTierName}
              </Badge>
            </div>

            <IuiSidebarSeparator className="bg-border/30" />

            {/* Logout Button */}
            <Button
              variant="ghost"
              onClick={() => logout()}
              className="h-auto w-full justify-start gap-3 px-4 py-3 text-supporting hover:bg-coral/5 hover:text-coral"
            >
              <LogOut className="h-5 w-5 flex-shrink-0" />
              <div className="flex-1 text-left">
                <div className="font-medium">Sign out</div>
                <div className="mt-0.5 text-xs text-supporting">Sign out of your account</div>
              </div>
            </Button>
          </div>
        )}
      </IuiSidebarFooter>
    </>
  );
}

interface EnhancedNavigationSidebarProps {
  onNavigate?: () => void;
}

export function EnhancedNavigationSidebar({ onNavigate }: EnhancedNavigationSidebarProps) {
  return (
    <IuiSidebarProvider
      defaultOpen={true}
      style={{ "--sidebar-width": "17rem" } as React.CSSProperties}
    >
      <IuiSidebar
        collapsible="icon"
        variant="sidebar"
        className="bg-background/60 backdrop-blur"
      >
        <SidebarContentInner onNavigate={onNavigate} />
      </IuiSidebar>
    </IuiSidebarProvider>
  );
}
